import { Component } from '@theme/component';

/**
 * @typedef {object} KatuaHeroRefs
 * @property {HTMLElement} track - The row of cards.
 * @property {HTMLElement} dots - Container the review dots are built into.
 * @property {HTMLElement} [review] - Review card that rotates through the cards' reviews.
 * @property {HTMLButtonElement} [pauseButton] - Pauses or resumes the marquee, videos and reviews.
 */

/**
 * Katua UGC hero. The cards run as a continuous marquee: the script repeats the original cards so
 * one set is wider than the viewport, appends a copy of that set (hidden from assistive tech) and
 * CSS animates the row by exactly one set, so the loop is seamless at a constant px/s speed.
 *
 * Media loading: a card's video lives in a <template> and is only inserted while that card is on
 * screen; it plays muted and pauses when it leaves. Videos are skipped with Save-Data; with reduced
 * motion the row stays still, scrollable, without videos or review rotation.
 *
 * Everything pauses when the hero is off screen, the tab is hidden, or the shopper presses pause.
 *
 * @extends Component<KatuaHeroRefs>
 */
class KatuaHeroUgc extends Component {
  requiredRefs = ['track', 'dots'];

  /** @type {AbortController} */
  #listeners = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #sectionObserver;

  /** @type {IntersectionObserver | undefined} */
  #cardObserver;

  /** @type {WeakMap<Element, boolean>} */
  #cardVisible = new WeakMap();

  /** @type {HTMLTemplateElement[]} */
  #reviews = [];

  #reviewIndex = 0;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #reviewTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #reviewResume;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resizeTimeout;

  #lastWidth = 0;
  #inView = false;
  #userPaused = false;
  #reviewHold = false;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    document.addEventListener('visibilitychange', () => this.#sync(), { signal });
    window.addEventListener('resize', this.#handleResize, { passive: true, signal });
    this.#reducedMotion.addEventListener(
      'change',
      () => {
        this.#setupMarquee();
        this.#sync();
      },
      { signal }
    );
    this.refs.review?.addEventListener('mouseenter', () => this.#holdReviews(true), { signal });
    this.refs.review?.addEventListener('mouseleave', () => this.#holdReviews(false), { signal });

    this.#setupMarquee();
    this.#setupReviews();

    this.#sectionObserver = new IntersectionObserver((entries) => {
      this.#inView = entries.some((entry) => entry.isIntersecting);
      this.#sync();
    });
    this.#sectionObserver.observe(this);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setupMarquee();
    this.#setupReviews();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners.abort();
    this.#sectionObserver?.disconnect();
    this.#cardObserver?.disconnect();
    clearInterval(this.#reviewTimer);
    clearTimeout(this.#reviewResume);
    clearTimeout(this.#resizeTimeout);
  }

  /**
   * Pauses or resumes the marquee, the videos and the review rotation.
   */
  togglePlayback() {
    this.#userPaused = !this.#userPaused;

    const { pauseButton } = this.refs;
    if (pauseButton) {
      pauseButton.setAttribute('aria-pressed', String(this.#userPaused));
      const label = this.#userPaused ? pauseButton.dataset.labelPlay : pauseButton.dataset.labelPause;
      if (label) pauseButton.setAttribute('aria-label', label);
    }

    this.#sync();
  }

  /**
   * @returns {HTMLElement[]} The cards rendered by Liquid (not the script's copies).
   */
  get #originals() {
    return Array.from(this.refs.track.querySelectorAll('[data-slide]:not([data-clone])')).filter(
      (slide) => slide instanceof HTMLElement
    );
  }

  get #isPlaying() {
    return this.#inView && !this.#userPaused && document.visibilityState === 'visible';
  }

  /**
   * Repeats the cards to fill the row twice over and starts the CSS animation.
   */
  #setupMarquee() {
    const { track } = this.refs;
    for (const clone of track.querySelectorAll('[data-clone]')) clone.remove();
    delete track.dataset.running;

    const originals = this.#originals;
    const container = track.parentElement;
    this.#lastWidth = container?.clientWidth ?? 0;

    const shouldRun = this.dataset.autoplay === 'true' && !this.#reducedMotion.matches && originals.length > 0;

    if (shouldRun && container) {
      const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
      const oneSet = originals.reduce((width, slide) => width + slide.offsetWidth + gap, 0);

      if (oneSet > 0) {
        const repeats = Math.max(1, Math.ceil(container.clientWidth / oneSet));
        const fragment = document.createDocumentFragment();

        for (let copy = 0; copy < repeats * 2 - 1; copy++) {
          for (const slide of originals) fragment.append(this.#cloneCard(slide));
        }

        track.append(fragment);
        const speed = Number(this.dataset.speed) || 35;
        track.style.setProperty('--katua-hero-duration', `${(oneSet * repeats) / speed}s`);
        track.dataset.running = '';
      }
    }

    this.#observeCards();
  }

  /**
   * @param {HTMLElement} slide
   * @returns {HTMLElement}
   */
  #cloneCard(slide) {
    const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
    clone.dataset.clone = '';
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('aria-label');
    clone.inert = true;

    // Copies must not be selectable as blocks in the theme editor.
    for (const attribute of Array.from(clone.attributes)) {
      if (attribute.name.startsWith('data-shopify')) clone.removeAttribute(attribute.name);
    }

    // Start copies from the poster; their own video is inserted when they come on screen.
    for (const video of clone.querySelectorAll('video')) video.remove();
    return clone;
  }

  #observeCards() {
    this.#cardObserver?.disconnect();
    this.#cardObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#cardVisible.set(entry.target, entry.isIntersecting);
          this.#syncCardVideo(entry.target);
        }
      },
      { threshold: 0.2 }
    );

    for (const slide of this.refs.track.querySelectorAll('[data-slide]')) {
      this.#cardObserver.observe(slide);
    }
  }

  /**
   * Inserts a visible card's video and plays it; pauses it otherwise.
   * @param {Element} slide
   */
  #syncCardVideo(slide) {
    const connection = /** @type {any} */ (navigator).connection;
    const allowVideo = !this.#reducedMotion.matches && !connection?.saveData;
    const shouldPlay = allowVideo && this.#isPlaying && this.#cardVisible.get(slide) === true;

    let video = slide.querySelector('video');

    if (shouldPlay && !video) {
      const template = slide.querySelector('template[data-video-template]');
      if (!(template instanceof HTMLTemplateElement)) return;

      const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
      video = fragment.querySelector('video');
      if (!video) return;

      const insertedVideo = video;
      insertedVideo.muted = true;
      insertedVideo.playsInline = true;
      insertedVideo.setAttribute('aria-hidden', 'true');
      insertedVideo.addEventListener('playing', () => insertedVideo.classList.add('katua-hero__video--playing'), {
        once: true,
      });
      template.after(fragment);
    }

    if (!video) return;

    if (shouldPlay) {
      video.play().catch(() => {
        // Autoplay can be blocked (e.g. low power mode); the poster stays visible.
      });
    } else {
      video.pause();
    }
  }

  #setupReviews() {
    const { review, dots } = this.refs;

    this.#reviews = this.#originals
      .map((slide) => slide.querySelector('template[data-review-template]'))
      .filter((template) => template instanceof HTMLTemplateElement);

    if (!review || this.#reviews.length === 0) {
      if (review) review.hidden = true;
      dots.replaceChildren();
      dots.hidden = true;
      return;
    }

    const labelTemplate = this.dataset.slideLabel || '[index] / [length]';
    const total = this.#reviews.length;

    dots.replaceChildren(
      ...this.#reviews.map((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'katua-hero__dot';
        dot.setAttribute(
          'aria-label',
          labelTemplate.replace('[index]', String(index + 1)).replace('[length]', String(total))
        );
        dot.addEventListener('click', () => {
          this.#showReview(index);
          this.#holdReviews(true);
          clearTimeout(this.#reviewResume);
          this.#reviewResume = setTimeout(() => this.#holdReviews(false), 8000);
        });
        return dot;
      })
    );
    dots.hidden = total < 2;

    this.#showReview(Math.min(this.#reviewIndex, total - 1));
  }

  /**
   * @param {number} index
   */
  #showReview(index) {
    const { review, dots } = this.refs;
    const template = this.#reviews[index];
    if (!review || !template) return;

    this.#reviewIndex = index;
    review.replaceChildren(template.content.cloneNode(true));
    review.hidden = false;

    Array.from(dots.children).forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
  }

  /**
   * @param {boolean} hold
   */
  #holdReviews(hold) {
    this.#reviewHold = hold;
    this.#syncReviewTimer();
  }

  #syncReviewTimer() {
    clearInterval(this.#reviewTimer);

    const rotate =
      this.#reviews.length > 1 && this.#isPlaying && !this.#reviewHold && !this.#reducedMotion.matches;
    if (!rotate) return;

    const interval = (Number(this.dataset.interval) || 5) * 1000;
    this.#reviewTimer = setInterval(() => {
      this.#showReview((this.#reviewIndex + 1) % this.#reviews.length);
    }, interval);
  }

  /**
   * Applies the playing/paused state to the marquee, the videos and the reviews.
   */
  #sync() {
    this.refs.track.toggleAttribute('data-paused', !this.#isPlaying);

    for (const slide of this.refs.track.querySelectorAll('[data-slide]')) {
      this.#syncCardVideo(slide);
    }

    this.#syncReviewTimer();
  }

  #handleResize = () => {
    clearTimeout(this.#resizeTimeout);
    this.#resizeTimeout = setTimeout(() => {
      const width = this.refs.track.parentElement?.clientWidth ?? 0;
      if (Math.abs(width - this.#lastWidth) < 40) return;
      this.#setupMarquee();
      this.#sync();
    }, 200);
  };
}

if (!customElements.get('katua-hero-ugc')) {
  customElements.define('katua-hero-ugc', KatuaHeroUgc);
}
